import axios from 'axios';

/**
 * Fetches the finance module authentication token after successful login
 * @param {number|string} userId - The user ID
 * @param {number|string} clinicId - The healthcare provider/clinic ID
 * @returns {Promise<string|null>} The finance auth token or null if failed
 */
export const getFinanceAuthToken = async (userId, clinicId) => {
  try {
    console.log('Fetching finance module token for userId:', userId, 'clinicId:', clinicId);


    // Step 1: Get module standalone data with  params
    const moduleUrl = `https://api.greenzonetechnologies.com.ng/medicalmodulerole/api/ModulesDetails/GetModuleStandAlone/GetModuleStandalone?clinicid=${clinicId}&userid=${userId}`;
    
    const moduleResponse = await axios.get(moduleUrl);

    if (moduleResponse.data?.code !== 1 || !moduleResponse.data?.data) {
      console.error('Failed to fetch module data:', moduleResponse.data);
      return null;
    }

    // Step 2: Find Finance module and extract emp value
    const financeCategory = moduleResponse.data.data.find(
      category => category.category === 'Finance'
    );

    if (!financeCategory || !financeCategory.module || financeCategory.module.length === 0) {
      console.error('Finance module not found in response');
      return null;
    }

    const financeModule = financeCategory.module[0];
    
    // Store the Finance module URL for navigation
    if (financeModule.address) {
      // Append practitioner URL for logout redirect
      const practitionerUrl = 'https://practitioners.connecthealthpro.com/landing';
      const financeUrlWithRedirect = `${financeModule.address}&practitionerUrl=${practitionerUrl}`;
      sessionStorage.setItem('financeModuleUrl', financeUrlWithRedirect);
    }
    
    // Extract emp value from the address URL
    const addressUrl = new URL(financeModule.address);
    const empValue = addressUrl.searchParams.get('emp');

    if (!empValue) {
      console.error('emp value not found in Finance module address');
      return null;
    }

    console.log('Finance emp value extracted:', empValue);

    // Step 3: Authenticate with finance backend using emp value
    const authUrl = `https://api.greenzonetechnologies.com.ng/healthfinanceapi/api/Auth/auth?AuthToken=${empValue}&iSStandalone=true`;
    
    const authResponse = await axios.post(authUrl);

    // Extract token from nested response structure: { resultList: { token: "..." } }
    if (authResponse.data?.resultList?.token) {
      console.log('Finance auth token obtained successfully');
      return authResponse.data.resultList.token;
    } else if (authResponse.data?.token) {
      console.log('Finance auth token obtained successfully (direct)');
      return authResponse.data.token;
    } else if (authResponse.data?.data?.token) {
      console.log('Finance auth token obtained successfully (nested in data)');
      return authResponse.data.data.token;
    } else {
      console.error('Token not found in auth response:', authResponse.data);
      return null;
    }

  } catch (error) {
    console.error('Error in getFinanceAuthToken:', error);
    if (error.response) {
      console.error('Response error:', error.response.data);
    }
    return null;
  }
};

/**
 * Get finance API config with the finance-specific token
 * @returns {object} Axios config object with finance token
 */
export const getFinanceConfig = () => {
  const financeToken = sessionStorage.getItem('financeToken');
  
  if (!financeToken) {
    console.warn('Finance token not found in session storage');
    return {
      headers: {
        'Content-Type': 'application/json'
      }
    };
  }

  return {
    headers: {
      'Authorization': financeToken, // No Bearer prefix for finance backend
      'Content-Type': 'application/json'
    }
  };
};
